<template v-if="canView(data)">
	<div>
		<v-list-item v-if="data.heading || data.subheading" :disabled="data.disabled">
			<v-list-item-content class="mt-2">
				<v-list-item-title
						v-if="data.heading"
						class="mb-2 mt-0 text-h6"
						tag="h2"
				>
					{{ data.heading }}
				</v-list-item-title>
				<v-list-item-subtitle v-if="data.subheading">
					{{ data.subheading }}
				</v-list-item-subtitle>
			</v-list-item-content>
		</v-list-item>

		<template v-if="canView(section)" v-for="(section, sectionIdx) in data.sections">
			<div
					v-if="Object.keys(section).length"
					:key="`section-${sectionIdx}`"
			>

				<v-list-item
						v-if="section.title"
						class="section-heading grey--text mb-0"
						:class="{
							'mt-6': sectionIdx > 0
						}"
						dense
						:disabled="section.disabled"
				>

					<v-list-item-icon>
						<v-icon
								:color="section.disabled ? 'grey' : 'grey darken-3'"
						>
							mdi-rhombus-medium
						</v-icon>
					</v-list-item-icon>

					<v-list-item-content style="flex: initial">
						<v-list-item-title
								:class="[
									'text-caption',
									'grey--text',
									'text-uppercase',
									{
										'text--darken-3': !section.disabled
									}
								]"
						>
							{{ section.title }}
						</v-list-item-title>
					</v-list-item-content>
					<v-divider class="flex-grow-1 ms-2 my-0"></v-divider>
				</v-list-item>

				<v-list-item
						v-if="section.description"
						class="section-heading grey--text mb-0 mb-2 mt-n2"
						dense
						:disabled="section.disabled"
				>

					<v-list-item-icon>
						<v-icon
								color="grey lighten-2"
								style="transform: rotate(45deg);"
						>
							mdi-chevron-down
						</v-icon>
					</v-list-item-icon>

					<v-list-item-content :style="{
						flex: 'initial',
						overflow: 'initial',
						textOverflow: 'initial',
						whiteSpace: 'initial'
				}">
						<v-list-item-title
								:class="[
									'mt-1',
									'text-caption',
									'grey--text',
									'text--darken-1'
							]"
								style="white-space: initial;"
						>
							{{ section.description }}
						</v-list-item-title>
					</v-list-item-content>
				</v-list-item>

				<template v-if="canView(step)" v-for="(step, stepIdx) in section.steps">
					<div v-if="step.name" :key="`step-${stepIdx}`">
						<v-list-item
								:class="{ 'v-list-item--active': step.active }"
								dense
								:disabled="section.disabled || step.disabled"
								:href="step?.href"
						>
							<v-list-item-icon>
								<v-icon
										v-if="step.disabled"
										:key="`step-${stepIdx}-disabled`"
										class="ms-1"
										:color="colors.grey.base"
										:size="16"
								>
									mdi-circle-outline
								</v-icon>
								<v-icon
										v-else-if="step.complete"
										:key="`step-${stepIdx}-complete`"
										:color="
										step.active ? 'var(--v-success-darken2)' : 'var(--v-success-base)'
									"
								>
									mdi-check-circle
								</v-icon>
								<v-icon
										v-else
										:key="`step-${stepIdx}-incomplete`"
										:color="
										step.active ? 'var(--v-success-base)' : colors.grey.base
									"
								>
									{{
										section.sequential
												? `mdi-numeric-${stepIdx + 1}-circle`
												: 'mdi-arrow-right-drop-circle'
									}}
								</v-icon>
							</v-list-item-icon>

							<v-list-item-content>
								<v-list-item-title :class="{ required: step.required }">
									{{ step.name }}
								</v-list-item-title>
							</v-list-item-content>
							<v-list-item-icon v-if="step.count && !step.disabled">
								<v-icon
										:color="step?.countColor ? `var(--v-${step.countColor}-base)` : 'var(--v-success-base)'">
									{{
										step.count > 9
												? `mdi-numeric-9-plus-circle`
												: `mdi-numeric-${step.count}-circle`
									}}
								</v-icon>
							</v-list-item-icon>
						</v-list-item>
					</div>
				</template>
			</div>
		</template>
	</div>
</template>

<script>
import colors from 'vuetify/lib/util/colors';

export default {
	name: 'Stepper',
	components: {},
	props: {
		data: {
			type: Object,
			default: () => ({})
		},
		isAdmin: Boolean,
		navigateWithin: Boolean
	},
	data() {
		return {
			colors,
			EventBus: window.EventBus
		};
	},
	created() {
		this.applyNonAdminDisabled(this.data);
	},
	computed: {
		formIsDirty() {
			return this.$store.state.formState.isDirty;
		}
	},
	methods: {
		applyNonAdminDisabled(item) {
			// If item is at the root level or section level, apply the logic
			if(item.nonAdminDisabled && !this.isAdmin) {
				item.disabled = true;
			}

			// If root level, apply to all sections and steps
			if(item.sections) {
				item.sections.forEach(section => {
					this.applyNonAdminDisabled(section);
					// Cascade down disabled status if the root is disabled
					if(item.disabled) {
						section.disabled = true;
						section.steps.forEach(step => {
							step.disabled = true;
						});
					}
				});
			}

			// If section level, apply to all steps
			if(item.steps?.length) {
				item.steps.forEach(step => {
					if(item.disabled) {
						step.disabled = true;
					}
					else if(step.nonAdminDisabled && !this.isAdmin) {
						step.disabled = true;
					}
				});
			}
		},

		canView(data) {
			return !data?.adminOnly || data?.adminOnly && this.isAdmin;
		}
	}
};
</script>

<style lang="scss" scoped>
.v-list-item {
	&--link:before {
		background: none !important;
	}

	&__title {
		&.required:after {
			color: red;
			content: " *";
		}
	}

	&__subtitle {
		overflow: initial;
		text-overflow: initial;
		white-space: initial;
	}
}

.incomplete-step {
	border-radius: 10px;
	color: white;
	font-weight: bold;
	height: 20px;
	line-height: 20px;
	margin: 1px;
	text-align: center;
	width: 20px;
}

::v-deep .v-list-item {
	border-radius: 0.25rem;

	&--active:not(:hover):not(:focus):before {
		opacity: 0.05 !important;
	}

	&::before {
		border-radius: 0.25rem;
	}
}

a.v-list-item--active {
	background: rgba(0, 0, 0, 0.04);
}
</style>
